/**
 * Mapper functions for converting API types to domain types.
 *
 * Transforms data from backend API format to frontend domain model format.
 * Handles type conversions and data normalization for partner finder and
 * messaging features.
 *
 * @module mappers
 */

import {
  Partner,
  PartnerRequest,
  DayOfWeek,
  GermanLevel,
  Message,
  RequestStatus,
} from '@/types/partner-finder';
import type { PartnerRequestAPI, MessageAPI } from '@/types/api/partner';
import { VALID_DAYS } from '@/lib/constants';

/**
 * Maps an API PartnerRequestAPI to a Partner domain type.
 *
 * Transforms backend partner request data into the frontend Partner format used
 * for displaying partners in search results. Filters and validates day-of-week
 * values to ensure type safety.
 *
 * @param request - The API partner request object from backend
 * @returns Partner domain object with transformed fields
 *
 * @example
 * ```typescript
 * const partner = mapApiRequestToPartner(apiRequest);
 * // partner.displayName comes from apiRequest.userName
 * // partner.meetingPreference is 'online' or 'in-person' based on apiRequest.isOnline
 * ```
 */
export function mapApiRequestToPartner(
  request: PartnerRequestAPI,
): Partner {
  // Map suggestedDays (string[]) to availability (DayOfWeek[])
  // Backend should return full day names, filter to ensure type safety
  const availability: DayOfWeek[] = request.suggestedDays.filter((day): day is DayOfWeek =>
    VALID_DAYS.has(day as DayOfWeek),
  );

  return {
    id: request.id,
    userId: request.userId,
    displayName: request.userName,
    level: request.level as GermanLevel,
    goal: request.goal,
    meetingPreference: request.isOnline ? 'online' : 'in-person',
    availability,
    location: request.location,
    about: request.moreInfo ?? '',
  };
}

/**
 * Formats a timestamp to a human-readable relative time string.
 *
 * Converts an ISO timestamp to a relative time format (e.g., "5m ago", "2h ago").
 * Handles recent times (minutes/hours), yesterday, and older dates.
 *
 * @param timestamp - ISO timestamp string (e.g., "2024-12-18T10:30:00Z")
 * @returns Formatted time string:
 *   - "Just now" for < 1 minute
 *   - "5m ago" for < 1 hour
 *   - "2h ago" for < 24 hours
 *   - "Yesterday" for < 48 hours
 *   - Date string for older dates
 *
 * @example
 * ```typescript
 * formatTime('2024-12-18T10:30:00Z'); // Returns "2h ago" (if current time is 12:30)
 * formatTime('2024-12-17T10:30:00Z'); // Returns "Yesterday"
 * ```
 */
export function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    const diffInMinutes = Math.floor(diffInHours * 60);
    return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString();
  }
}

/**
 * Extracts initials from a name string.
 *
 * Takes the first letter of each word in the name and returns them as uppercase
 * initials, limited to a maximum of 2 characters.
 *
 * @param name - Full name string (e.g., "John Doe", "Maria Garcia")
 * @returns Uppercase initials string (max 2 characters), e.g., "JD", "MG"
 *
 * @example
 * ```typescript
 * getInitials('John Doe'); // Returns "JD"
 * getInitials('Maria Garcia Lopez'); // Returns "MG" (only first 2 words)
 * getInitials('Anna'); // Returns "A"
 * ```
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}


/**
 * Maps API messages to Message domain objects for a specific conversation.
 *
 * Filters messages to only include those between the current user and the
 * conversation partner, then transforms them to the Message domain format
 * and sorts by timestamp.
 *
 * @param messages - Array of API messages from backend
 * @param conversationId - Conversation ID in format "conv-{userId}"
 * @param currentUserId - Current user ID for filtering messages
 * @returns Array of Message domain objects sorted chronologically (oldest first)
 *
 * @example
 * ```typescript
 * const conversationMessages = mapMessagesForConversation(
 *   allMessages,
 *   'conv-user-456',
 *   'user-123'
 * );
 * // Returns only messages between user-123 and user-456
 * ```
 */
export function mapMessagesForConversation(
  messages: MessageAPI[],
  conversationId: string,
  currentUserId: string,
): Message[] {
  const partnerId = conversationId.replace('conv-', '');
  return messages
    .filter((msg) => {
      const otherUserId = msg.fromUserId === currentUserId ? msg.toUserId : msg.fromUserId;
      return otherUserId === partnerId;
    })
    .map((msg) => ({
      id: msg.id,
      conversationId,
      senderId: msg.fromUserId,
      content: msg.content,
      timestamp: msg.createdAt,
      isSystem: false,
    }))
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

/**
 * Maps an API PartnerRequestAPI to a PartnerRequest domain type.
 *
 * Transforms backend partner request data into the frontend PartnerRequest format
 * used for editing and managing user's own requests. Maps isActive boolean to
 * RequestStatus enum and validates day-of-week values.
 *
 * @param request - The API partner request object from backend
 * @returns PartnerRequest domain object with all fields mapped and validated
 *
 * @example
 * ```typescript
 * const partnerRequest = mapApiRequestToPartnerRequest(apiRequest);
 * // partnerRequest.status is 'active' or 'paused' based on apiRequest.isActive
 * // partnerRequest.availability is filtered and validated DayOfWeek array
 * ```
 */
export function mapApiRequestToPartnerRequest(
  request: PartnerRequestAPI,
): PartnerRequest {
  // Map suggestedDays to availability (DayOfWeek[])
  // Backend should return full day names, filter to ensure type safety
  const availability: DayOfWeek[] = request.suggestedDays.filter((day): day is DayOfWeek =>
    VALID_DAYS.has(day as DayOfWeek),
  );

  // Map isActive to status
  let status: RequestStatus = 'active';
  if (!request.isActive) {
    status = 'paused';
  }

  return {
    id: request.id,
    userId: request.userId,
    level: request.level as GermanLevel,
    meetingPreference: request.isOnline ? 'online' : 'in-person',
    goal: request.goal,
    availability,
    location: request.location,
    additionalInfo: request.moreInfo ?? '',
    status,
    createdAt: request.createdAt,
    updatedAt: request.updatedAt,
  };
}

import { SimulationResult, ModuleResult, ExamType, ExamLevel } from '@/types/simulation-results';
import { ApiSimulationSession, ApiModuleResult } from './simulationResultsApi';

/**
 * Maps an API simulation session to a domain SimulationResult object.
 * 
 * @param apiSession - The session object from the backend API
 * @returns Transformed SimulationResult for the UI
 */
export function mapApiSimulationToDomain(apiSession: ApiSimulationSession): SimulationResult {
  return {
    id: apiSession.sessionId,
    date: apiSession.createdAt, // Keep full timestamp for time display
    examType: apiSession.examType as ExamType,
    level: apiSession.level as ExamLevel,
    totalScore: apiSession.totalScore,
    maxTotalScore: apiSession.maxTotalScore,
    percentage: apiSession.percentage,
    passed: apiSession.passed,
    duration: 0, // Duration not currently tracked in DB
    modules: apiSession.modules.map(mapApiModuleResultToDomain),
  };
}

/**
 * Maps an API module result to a domain ModuleResult object.
 * 
 * @param apiModule - The module result object from the backend API
 * @returns Transformed ModuleResult for the UI
 */
export function mapApiModuleResultToDomain(apiModule: ApiModuleResult): ModuleResult {
  return {
    module: apiModule.module.toLowerCase() as any,
    score: apiModule.score,
    maxScore: 100, // Assuming 100 as base for percentage scores
    percentage: apiModule.score,
    passed: apiModule.passed,
    is_graded: apiModule.is_graded,
    analysis_status: apiModule.analysis_status,
    analysis_error_code: apiModule.analysis_error_code,
    analysis_attempts: apiModule.analysis_attempts,
    analysis_can_retry: apiModule.analysis_can_retry,
    analysis_updated_at: apiModule.analysis_updated_at,
    section_feedback_status: apiModule.section_feedback_status ?? null,
  };
}


import { ModuleAnswer } from '@/types/simulation-results';
import { normalizeAnalysisBreakdown } from '@/lib/analysisBreakdown';

/**
 * Maps a raw API answer record to a domain ModuleAnswer object.
 * 
 * @param answer - The raw answer record from the API
 * @returns Transformed ModuleAnswer for the UI
 */
export function mapApiModuleAnswerToDomain(answer: any): ModuleAnswer {
  const normalizeText = (value: any) => {
    if (value !== null && value !== undefined) {
      const str = String(value).trim();
      return str.length > 0 ? str : undefined;
    }
    return undefined;
  };

  const optionToText = (option: unknown): string => {
    // After the Diagnostic Feedback System options-shape backfill, options are
    // stored as { text, tags, tag_confidence }. Earlier rows were plain strings.
    // Render the text in both cases.
    if (option && typeof option === "object" && "text" in option) {
      return String((option as { text: unknown }).text);
    }
    return String(option);
  };

  const normalizeOptions = (options: any): string[] | undefined => {
    if (options) {
      if (Array.isArray(options)) return options.map(optionToText);
      try {
        const parsed = JSON.parse(options);
        if (Array.isArray(parsed)) return parsed.map(optionToText);
      } catch {
        // ignore
      }
      return [optionToText(options)];
    }
    return undefined;
  };

  const sectionNumber = Number(answer.section);
  const numericPoints = answer.points === null ? null : Number(answer.points);
  const audioDuration = answer.audio_duration_seconds === null ? null : Number(answer.audio_duration_seconds);
  const questionOrder = Number(answer.question_order);
  // The 1..N position the learner saw during the (possibly shuffled) exam.
  // Without this the review page falls back to authored question_order and
  // reorders questions/tasks differently from the exam. NULL for legacy rows.
  const displayOrder =
    answer.display_order === null || answer.display_order === undefined
      ? null
      : Number(answer.display_order);

  // The backend stores an authoritative is_correct boolean (e.g. AI grading
  // uses points ≥ 0.6 for speaking/writing; binary modules write true/false
  // directly). Only fall back to a points heuristic when the backend hasn't
  // committed a decision yet (NULL while pending grading). Treating "any
  // positive points" as correct would flip low-scoring speaking answers
  // (0.1–0.2 of 1.0) into a "Correct" badge despite is_correct=false.
  let resolvedIsCorrect: boolean | undefined;
  if (typeof answer.is_correct === 'boolean') {
    resolvedIsCorrect = answer.is_correct;
  } else if (numericPoints !== null && Number.isFinite(numericPoints)) {
    resolvedIsCorrect = numericPoints >= 0.6;
  } else {
    resolvedIsCorrect = undefined;
  }

  return {
    id: String(answer.id),
    question_text: normalizeText(answer.question_text) || answer.question_id || 'Question',
    question_type: normalizeText(answer.question_type) || normalizeText(answer.type) || 'unknown',
    explanation: normalizeText(answer.explanation),
    context_id: answer.context_id ? String(answer.context_id) : null,
    context_content: normalizeText(answer.context_text_content) || normalizeText(answer.context_content),
    context_name: normalizeText(answer.context_name),
    context_type: normalizeText(answer.context_type),
    context_transcript: normalizeText(answer.context_transcript),
    context_audio_base64: normalizeText(answer.context_audio_base64),
    context_image_base64: normalizeText(answer.context_image_base64),
    context_media_url: normalizeText(answer.context_media_url),
    context_mime_type: normalizeText(answer.context_mime_type),
    answer_text: normalizeText(answer.answer_text),
    answer_audio_base64: normalizeText(answer.answer_audio_base64),
    audio_url: normalizeText(answer.audio_url),
    options: normalizeOptions(answer.options),
    expected_answer: normalizeText(answer.expected_answer),
    is_correct: resolvedIsCorrect,
    section: Number.isFinite(sectionNumber) ? sectionNumber : undefined,
    question_order: Number.isFinite(questionOrder) ? questionOrder : undefined,
    display_order: displayOrder !== null && Number.isFinite(displayOrder) ? displayOrder : null,
    audio_format: normalizeText(answer.audio_format),
    audio_duration_seconds: audioDuration,
    points: numericPoints,
    analysis_breakdown: normalizeAnalysisBreakdown(answer.analysis_breakdown),
    chosen_tags: Array.isArray(answer.chosen_tags) ? answer.chosen_tags : [],
    diagnostic_feedback: Array.isArray(answer.diagnostic_feedback)
      ? answer.diagnostic_feedback
      : [],
    diagnosis_status: answer.diagnosis_status ?? null,
  };
}
